/*
# Boundless Embedded Application

This bundle registers a `<boundless-embed>` custom element that encapsulates the
Boundless embed application.

Put the element anywhere in the DOM and supply the required attributes:

  <script src="https://app.getboundless.ai/embed.js"></script>

  ...

  <boundless-embed
    partner-id="partner_123"
    user-id="user_456"
  ></boundless-embed>

The `email`, `first-name`, and `last-name` attributes are optional. When the
partner already collected them during onboarding, they pre-populate the
create-account form (the `email` also seeds the sign-in form). Every field
stays editable. They are read once at mount. Because they are client-supplied,
they are a convenience only — not a trusted or verified identity.

  <boundless-embed
    partner-id="partner_123"
    user-id="user_456"
    email="jane@example.com"
    first-name="Jane"
    last-name="Doe"
  ></boundless-embed>

The element creates an isolated shadow DOM, mounts the React application, and
dispatches a `boundlessEmbedReady` event once rendering completes. The event
detail contains an object with an `unmount()` helper in case the host page wants
to remove the widget imperatively.
*/

declare const __INTERNAL_API_URL__: string

import datepickerCss from "react-datepicker/dist/react-datepicker.css?inline"
import ReactDOM from "react-dom/client"
import sonnerCss from "sonner/dist/styles.css?inline"

import { BoundlessEmbedWrapper, type UserConfig } from "@/embed/BoundlessEmbed"
import { resetApiBaseUrl, setApiBaseUrl } from "@/lib/clients"
import embedCss from "@/styles.css?inline"

type MountContext = {
  hostElement: HTMLElement
  parameters: { partnerId: string; userId: string }
  unmount: () => void
}

export type EmbedConfig = UserConfig & {
  apiBaseUrl: string
}

class BoundlessEmbedElement extends HTMLElement {
  #root: ShadowRoot
  private mountContext?: MountContext

  static get observedAttributes(): string[] {
    return ["partner-id", "user-id"]
  }

  constructor() {
    super()
    console.log("[boundless] <boundless-embed> component instantiated.")
    this.#root = this.attachShadow({ mode: "closed" })
  }

  connectedCallback() {
    // react-datepicker relies on react-onclickoutside, which listens on document
    // and treats clicks that don't originate from its calendar tree as "outside".
    // Inside a shadow DOM, events are retargeted so clicks on the calendar appear
    // to come from the <boundless-embed> host element and get treated as outside,
    // causing the picker to close without selecting a date.
    //
    // Adding the `react-datepicker-ignore-onclickoutside` class to the host tells
    // react-onclickoutside to ignore those events for this element, so date
    // selections inside the embed work correctly.
    this.classList.add("react-datepicker-ignore-onclickoutside")
    this.tryMount()
  }

  disconnectedCallback() {
    this.teardown()
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return
    if (!this.isConnected) return

    const config = this.readConfig()

    this.teardown()
    this.mountWithConfig(config)
  }

  private tryMount() {
    if (this.mountContext) return
    const config = this.readConfig()
    if (!config) return
    this.mountWithConfig(config)
  }

  private mountWithConfig(config: EmbedConfig) {
    const style = document.createElement("style")
    style.textContent = `${embedCss}\n${datepickerCss}\n${sonnerCss}`
    this.#root.appendChild(style)

    const mountPoint = document.createElement("div")
    mountPoint.dataset.theme = "boundless"
    // Fix height (e.g. bug related to jumping of the content when rendering the app)
    // and background color of embed app.
    mountPoint.className = "h-full bg-background"
    this.#root.appendChild(mountPoint)
    setApiBaseUrl(config.apiBaseUrl)

    const reactRoot = ReactDOM.createRoot(mountPoint, {})
    reactRoot.render(
      <BoundlessEmbedWrapper
        partnerId={config.partnerId}
        userId={config.userId}
        email={config.email}
        firstName={config.firstName}
        lastName={config.lastName}
        portalContainer={mountPoint}
        turnstileFrame={{
          frameUrl: new URL("/embed/turnstile/", config.apiBaseUrl).toString(),
        }}
      />,
    )

    const mountContext: MountContext = {
      hostElement: this,
      parameters: { partnerId: config.partnerId, userId: config.userId },
      unmount: () => {
        try {
          reactRoot.unmount()
        } catch (err) {
          console.error("Error during unmount:", err)
        } finally {
          style.remove()
          mountPoint.remove()
          resetApiBaseUrl()
        }
      },
    }

    this.mountContext = mountContext

    this.dispatchEvent(
      new CustomEvent("boundlessEmbedReady", { detail: mountContext }),
    )
  }

  private teardown() {
    if (!this.mountContext) return
    try {
      this.mountContext.unmount()
    } finally {
      this.mountContext = undefined
    }
  }

  private readConfig(): EmbedConfig {
    const partnerId = this.getAttribute("partner-id")?.trim()
    const userId = this.getAttribute("user-id")?.trim()

    if (!partnerId || !userId) {
      throw new Error(
        "[boundless] <boundless-embed> requires attributes `partner-id` and `user-id`.",
      )
    }

    // Optional prefill attributes. Normalize empty-after-trim to null so that
    // an absent attribute and an empty one collapse to a single no-prefill
    // state. Read once at mount; not observed for runtime changes.
    const email = this.getAttribute("email")?.trim() || null
    const firstName = this.getAttribute("first-name")?.trim() || null
    const lastName = this.getAttribute("last-name")?.trim() || null

    return {
      partnerId,
      userId,
      email,
      firstName,
      lastName,
      apiBaseUrl: __INTERNAL_API_URL__,
    }
  }
}

if (!customElements.get("boundless-embed")) {
  customElements.define("boundless-embed", BoundlessEmbedElement)
  console.log("[boundless] <boundless-embed> component registered.")
} else {
  console.log("[boundless] <boundless-embed> component already registered.")
}
